import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";

import { App } from "./App";
import "./styles/globals.css";

// Sentry init runs BEFORE React mounts so any error during mount or first
// render is captured. DSN comes from VITE_SENTRY_DSN at build time;
// missing/empty DSN skips init entirely. Even without the guard the Sentry
// SDK no-ops cleanly on falsy DSN, but the explicit if keeps init+config
// out of the dev console when we never intended to ship telemetry.
const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "dev",
    integrations: [
      // browserTracingIntegration captures route changes + outbound fetch
      // spans. Cheap and useful — Sentry's UI plots them against errors.
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    // Don't ship PII; the backend never sends PII to the frontend but
    // belt-and-braces against future regression.
    sendDefaultPii: false,
    // The release tag matches what the backend reports (APP_VERSION). The
    // deploy pipeline injects VITE_APP_VERSION from the same source if/when
    // we want strict alignment. Until then, leave undefined and Sentry
    // groups errors per-DSN, which is fine.
  });
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found; check index.html.");
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
